import { TextField } from '@material-ui/core'
import Autocomplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete'
import { debounce } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RequestStatus } from '../../../services/APIs/BaseAPI'
import {
  PartyDto,
  PartySearchResult,
} from '../../../services/APIs/InternalAPI/internal-api.contracts'
import { SearchPartyAPI } from '../../../services/APIs/InternalAPI/SearchPartyAPI'
import { ErrorMessage } from '../../../store/Application/appActions'
import { useOrganizationAndProjectContext } from '../../../utils/useOrganizationContext'
import { FindPartyResult } from './FindPartyResult'

type Props = {
  onPartyDetailsReceived: (buyerPartyDto: PartyDto) => void
  onPartyDetailsLoading?: (isLoading: boolean) => void
  onPartyDetailsCleaned: () => void
  searchPortalParties?: boolean
} & Partial<AutocompleteProps<PartySearchResult, false, false, false>>

const debouncedSearch = debounce(
  async (
    API: SearchPartyAPI,
    searchText,
    onRequestChange: (req: Partial<RequestStatus>) => void,
    onRequestFinish: (options) => void,
    searchPortalParties?: boolean
  ) => {
    try {
      const options = searchPortalParties
        ? (await API.SearchPortalParties(searchText, onRequestChange)).customers
        : await API.SearchParty(searchText, onRequestChange)

      onRequestFinish(options || [])
      return options
    } catch (err) {
      ErrorMessage('search parties', err['message'])
      return []
    }
  },
  1000,
  { leading: false, trailing: true }
)

export const SearchParty = (props: Props) => {
  const { t } = useTranslation()
  const {
    onPartyDetailsReceived,
    onPartyDetailsLoading,
    onPartyDetailsCleaned,
    ...autoCompleteProps
  } = props
  const [searchText, setSearchText] = useState<string>()
  const [options, setOptions] = useState<PartySearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { partyId: organizationId } = useOrganizationAndProjectContext()

  const API = new SearchPartyAPI(organizationId)

  const getDetails = async (findResult: PartySearchResult) => {
    if (findResult) {
      try {
        const details = await API.GetPartyDetails(
          findResult.searchKey,
          (req) => onPartyDetailsLoading && onPartyDetailsLoading(req.isLoading)
        )

        onPartyDetailsReceived(details)
      } catch (err) {
        if (err['message'] !== 'cancelled')
          ErrorMessage('search', err['message'])
      }
    } else {
      onPartyDetailsCleaned()
    }
  }

  const handleSearchTextChanged = (e) => {
    {
      const value = e.currentTarget.value

      if (value.length < 3) {
        setOptions([])
        debouncedSearch.cancel()
        setIsLoading(false)
      }

      if (value.length >= 3) {
        if (
          value < searchText ||
          !options.filter((x) => x.names[0].toLowerCase().startsWith(value))
            .length
        ) {
          setIsLoading(true)
          debouncedSearch(
            API,
            e.currentTarget.value,
            () => true,
            (options) => {
              setIsLoading(false)
              setOptions(options)
            },
            props.searchPortalParties
          )
        }
      }

      setSearchText(value)
    }
  }
  return (
    <Autocomplete
      {...autoCompleteProps}
      debug
      autoComplete
      onChange={(ev, value) => getDetails(value)}
      autoHighlight
      options={options}
      loading={isLoading}
      filterOptions={(options) => options} //not needed to filter the results
      getOptionLabel={(option) => {
        return option.names[0]
      }}
      getOptionSelected={(option, value) => {
        return option.searchKey === value.searchKey
      }}
      clearOnEscape
      renderOption={(option) => {
        return <FindPartyResult option={option} searchText={searchText} />
      }}
      renderInput={(props) => {
        return (
          <TextField
            {...props}
            variant={'standard'}
            label={`find a company`}
            placeholder={t('common:search')}
            inputProps={{
              ...props.inputProps,
              autoComplete: 'new-password',
            }}
            onChange={handleSearchTextChanged}
            value={searchText}
            autoComplete="new-password"
          />
        )
      }}
    />
  )
}
